// src/widget.js
// Entry point: reads config from a [data-visamundi-widget] container, mounts into a
// shadow root, loads countries, and runs a lookup whenever both selects are set
// (mirrors the change-driven behaviour of ../visamundi-widget/src/js/script.js).
import { detectLanguage, t } from './i18n.js';
import { fetchCountries, fetchRequirement, DEFAULT_API_BASE } from './api.js';
import { formatResult, buildVisamundiUrl } from './format.js';
import { renderSkeleton, fillCountries, renderResult, renderError, renderLoading, cheerMascot } from './render.js';
import { destinationUrl } from './destinations-url.js';

const MOUNTED = '__vmWidgetMounted';

function readOptions(el) {
  const d = el.dataset;
  const lang = d.language
    ? detectLanguage(d.language)
    : detectLanguage(document.documentElement.lang || 'en');
  return {
    language: lang,
    theme: d.theme === 'dark' ? 'dark' : 'light',
    size: d.size === 'compact' ? 'compact' : 'regular',
    partnerId: d.partnerId || '',
    affilaeId: d.affilaeId || '',
    defaultNationality: d.defaultNationality || '',
    defaultDestination: d.defaultDestination || '',
    key: d.key || '',
    apiBase: d.apiBase || DEFAULT_API_BASE,
  };
}

export async function mount(el, optionOverrides = {}) {
  if (!el || el[MOUNTED]) return;
  el[MOUNTED] = true;

  const opts = { ...readOptions(el), ...optionOverrides };
  const refs = renderSkeleton(el, opts);

  // Only the latest lookup may render: a slow earlier response must not
  // overwrite the answer for the pair currently selected.
  let seq = 0;
  const run = async () => {
    const nat = refs.nationality.value;
    const dest = refs.destination.value;
    if (!nat || !dest) return;
    const mine = ++seq;
    renderLoading(refs.result, t(opts.language, 'loading'));
    try {
      const data = await fetchRequirement({
        apiBase: opts.apiBase,
        language: opts.language,
        nationality: nat,
        destination: dest,
        key: opts.key,
      });
      if (mine !== seq) return;
      if (data.visamundi_url) {
        data.visamundi_url = buildVisamundiUrl(data.visamundi_url, {
          partnerId: opts.partnerId,
          affilaeId: opts.affilaeId,
        });
      }
      // If the destination is a Visamundi destination, offer its landing page.
      const destPage = destinationUrl(dest, opts.language);
      const extra = destPage
        ? {
            label: t(opts.language, 'discoverDestination'),
            url: buildVisamundiUrl(destPage, {
              partnerId: opts.partnerId,
              affilaeId: opts.affilaeId,
            }),
          }
        : null;
      const destName = data?.i18n?.destination?.country?.name || '';
      renderResult(refs.result, formatResult(data, opts.language, { sameCountry: nat === dest }), {
        extra,
        destination: { name: destName, alpha3: dest },
      });
      cheerMascot(refs.mascot);
    } catch (_) {
      if (mine === seq) renderError(refs.result, t(opts.language, 'error'));
    }
  };

  refs.nationality.addEventListener('change', run);
  refs.destination.addEventListener('change', run);

  try {
    const countries = await fetchCountries({
      apiBase: opts.apiBase,
      language: opts.language,
      key: opts.key,
    });
    fillCountries([refs.nationality], countries, opts.defaultNationality);
    fillCountries([refs.destination], countries, opts.defaultDestination);
    if (refs.nationality.value && refs.destination.value) run();
  } catch (_) {
    renderError(refs.result, t(opts.language, 'error'));
  }
}

export function mountAll(root = document) {
  root.querySelectorAll('[data-visamundi-widget]').forEach((el) => mount(el));
}

function autoInit() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAll());
  } else {
    mountAll();
  }
}

const api = { mount, mountAll };
if (typeof window !== 'undefined') {
  window.VisamundiWidget = api;
  autoInit();
}

export default api;
